'use client';

import classNames from '@sindresorhus/class-names';
import {HeaderProps} from '@ui-kit/src/Header/Header.types';
import {headerTestId} from '@ui-kit/src/Header/headerTestid';
import {useEffect, useState} from 'react';

import styles from './Anchors.module.scss';

type Props = Pick<
  HeaderProps,
  'anchorLinks' | 'onAnchorLinkClick' | 'isRedesignAnchors'
>;

const SCROLL_THRESHOLD = 96;

export const Anchors = ({
  anchorLinks,
  onAnchorLinkClick,
  isRedesignAnchors,
}: Props) => {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    if (!window?.IntersectionObserver) {
      return;
    }

    if (isRedesignAnchors) {
      const scrollMarker = document.createElement('div');
      scrollMarker.style.position = 'absolute';
      scrollMarker.style.top = `${SCROLL_THRESHOLD}px`;
      scrollMarker.style.height = '1px';
      scrollMarker.style.width = '0';
      document.body.appendChild(scrollMarker);

      const observer = new IntersectionObserver(
        ([entry]) => {
          setIsScrolled(!entry.isIntersecting);
        },
        {
          threshold: 1.0,
          rootMargin: '0px',
        },
      );

      observer.observe(scrollMarker);

      return () => {
        observer.disconnect();
        document.body.removeChild(scrollMarker);
      };
    }
  }, [isRedesignAnchors]);

  if (!anchorLinks?.length) return null;

  return (
    <div
      className={classNames(styles.wrapper, {
        [styles.redesignWrapper]: isRedesignAnchors,
      })}
    >
      <div
        className={classNames(styles.links, {
          [styles.redesignLinks]: isRedesignAnchors,
          [styles.linksBackground]: isRedesignAnchors && isScrolled,
        })}
        data-testid="anchorsLinks"
      >
        {anchorLinks.map(({isActiveAnchor, ...link}) => (
          <a
            onClick={() => onAnchorLinkClick?.(link)}
            className={classNames(styles.link, {
              [styles.isActive]: isActiveAnchor,
              [styles.redesignLink]: isRedesignAnchors,
            })}
            {...link}
            key={link.title}
            data-testid={headerTestId.anchor}
          >
            {link.title}
          </a>
        ))}
      </div>
    </div>
  );
};
